import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { firstDownloadUsageSeenAtom } from '@/atoms/firstDownloadUsage';
import { downloadStageContextAtom } from '@/components/DialogStage/atom';
import { toDownloadStageAsset } from '@/components/DialogStage/download-asset-mapping';
import { useStage } from '@/components/DialogStage/useStage';
import { conversionPoints } from '@/components/Modals/context/ModalRoutingContext';
import { ModalConversionPoints } from '@/components/Modals/shared-types';
import { useSubscriptionType } from '@/hooks/auth';
import { useSettings } from '@/hooks/state/useSettings';
import { recordConversion } from '@/utils/analytics/conversion';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

import type { DownloadJourney } from '@/components/DialogStage/atom';
import type { DownloadDecision } from '@/features/Downloads/Gateway/types';
import type { AssetGroup } from '@/types/asset';

/**
 * Gateway decisions that mean "an authenticated user is about to receive (or
 * be upsold on) a download" — the moment DEV-6530 detours a first-time
 * downloader into the "Where will you use this asset?" step instead of the
 * regular journey. `navigate` (sign-up / verify-email / overdue-payment) and
 * `pending` are deliberately excluded: those are pre-download gates that must
 * still resolve normally before any download can happen.
 */
export const isFirstDownloadTerminalDecision = (
  decision: DownloadDecision
): boolean =>
  decision.action === 'download' ||
  decision.action === 'ask-format' ||
  decision.action === 'upsell';

/**
 * The journey the usage step should run for this decision. A free user who
 * can't reach a premium asset (`upsell` / `premium-asset`) gets the premium
 * journey's plan selection; everyone else (free asset with credits, entitled
 * subscriber, out-of-credits free user) gets the free journey, whose CTAs
 * both start the entitled download and route to checkout.
 */
const journeyForDecision = (decision: DownloadDecision): DownloadJourney =>
  decision.action === 'upsell' && decision.reason === 'premium-asset'
    ? 'premium'
    : 'free';

/**
 * DEV-6530: is this authenticated user eligible for the "first download"
 * usage journey? True only when the new download flow is on, the user is
 * signed in, they have never downloaded (`download_count === 0`), and they
 * haven't already completed the step (the one-shot flag, set on an
 * own-content download — DEV-6626). Established users and anyone who has
 * already answered fall through to the regular journey.
 *
 * `download_count` is checked strictly against `0` rather than treating an
 * absent value as zero, so a settings payload that omits the field can't
 * misclassify an established user as a first-time downloader.
 *
 * Business subscribers are excluded outright: their plan already licenses
 * every usage the step asks about, so there is nothing to sell or gate —
 * they get the regular download journey from their very first download.
 */
export const useShouldOfferFirstDownloadUsage = (): boolean => {
  const newDownloadFlow = useFeatureIsOn('new-download-flow');
  const { settings } = useSettings();
  const seen = useAtomValue(firstDownloadUsageSeenAtom);
  const subscriptionType = useSubscriptionType();

  return (
    newDownloadFlow &&
    subscriptionType !== 'Business' &&
    Boolean(settings?.user.is_authenticated) &&
    settings?.user.download_count === 0 &&
    !seen
  );
};

/**
 * Opens the DialogStage usage step for an authenticated user's first
 * download (DEV-6530). Seeds the stage context from the asset the user
 * clicked, choosing the premium or free journey from the gateway decision.
 *
 * Deliberately does *not* mark the one-shot flag (DEV-6626): a user who
 * closes the modal without answering must get the question again on their
 * next download. The usage step sets the flag itself, only when an
 * own-content download starts.
 */
export const useOpenFirstDownloadUsage = () => {
  const { open } = useStage();
  const setDownloadContext = useSetAtom(downloadStageContextAtom);

  return useCallback(
    (asset: AssetGroup, decision: DownloadDecision) => {
      const journey = journeyForDecision(decision);

      setDownloadContext({
        asset: toDownloadStageAsset(asset),
        sourceAsset: asset,
        journey,
      });
      recordConversion({
        source:
          conversionPoints[
            journey === 'premium'
              ? ModalConversionPoints.DownloadFlowPremiumGate
              : ModalConversionPoints.DownloadFlowFirstDownload
          ],
        assetId: Number(asset.data.id),
        assetType: asset.type,
      });
      // A premium first download starts at the gate, so the user is told why
      // it didn't just download before being asked about usage (DEV-6713).
      open(journey === 'premium' ? 'download.premium-gate' : 'download.usage');
    },
    [open, setDownloadContext]
  );
};
